/**
 * useTripMembers - Load a trip's member list and merge in nickname/avatar via
 * useUserProfiles, plus wrap the role/remove/leave CRUD operations.
 * Subscribes to MEMBER_JOINED/MEMBER_ROLE_CHANGED/MEMBER_REMOVED (T036) — a
 * no-op unless called from within a TripRealtimeProvider. When the current
 * user is the one removed, navigates away and shows a toast instead of
 * refetching (a refetch would just 401).
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { memberService, MemberApiError } from '../services/memberService';
import { useUserProfiles, getAvatarFallback } from './useUserProfiles';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTripRealtimeSubscribe } from '../contexts/TripRealtimeContext';
import { REALTIME_EVENTS } from '../utils/realtimeEvents';
import type { MemberRole, TripMember } from '../types/member';

export interface MemberWithProfile extends TripMember {
  nickname: string;
  avatar: string | null;
}

export function useTripMembers(tripId: string | undefined) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [members, setMembers] = useState<TripMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { profiles } = useUserProfiles(members.map((m) => m.userId));

  const membersWithProfile: MemberWithProfile[] = members.map((member) => {
    const profile = profiles.get(member.userId);
    return {
      ...member,
      nickname: profile?.nickname ?? getAvatarFallback(undefined),
      avatar: profile?.avatar ?? null,
    };
  });

  const refetch = useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await memberService.getMembers(tripId);
      setMembers(list);
    } catch (err) {
      setError(err instanceof MemberApiError ? err.message : '取得成員清單失敗');
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useTripRealtimeSubscribe(REALTIME_EVENTS.MEMBER_JOINED, refetch);
  useTripRealtimeSubscribe(REALTIME_EVENTS.MEMBER_ROLE_CHANGED, refetch);
  useTripRealtimeSubscribe(REALTIME_EVENTS.MEMBER_REMOVED, (data) => {
    if (data.userId === user?.userId) {
      showToast('info', '您已被移出此旅程');
      navigate('/trips');
      return;
    }
    refetch();
  });
  useTripRealtimeSubscribe(REALTIME_EVENTS.RECONNECTED, refetch);

  const changeMemberRole = useCallback(
    async (targetUserId: number, role: MemberRole) => {
      if (!tripId) return;
      await memberService.changeMemberRole(tripId, targetUserId, role);
      await refetch();
    },
    [tripId, refetch]
  );

  const removeMember = useCallback(
    async (targetUserId: number) => {
      if (!tripId) return;
      await memberService.removeMember(tripId, targetUserId);
      await refetch();
    },
    [tripId, refetch]
  );

  const leaveTrip = useCallback(async () => {
    if (!tripId) return;
    await memberService.leaveTrip(tripId);
  }, [tripId]);

  return {
    members: membersWithProfile,
    loading,
    error,
    refetch,
    changeMemberRole,
    removeMember,
    leaveTrip,
  };
}
